import './autoCompleteDropdownComponent.scss'
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {
    Autocomplete,
    AutocompleteRenderOptionState,
    CircularProgress,
    FilterOptionsState,
    FormHelperText
} from "@mui/material";
import CommonService from "../../../services/common.service";
import {AXIOS_REQUEST_CANCELLED} from "../../../services/api.service";
import TextField from "@mui/material/TextField";
import {IAPIResponseType} from "../../../models/api.model";
import _ from "lodash";
import FormControl from '@mui/material/FormControl';

interface AutoCompleteDropdownComponentProps {
    label: string;
    size?: 'small' | 'medium';
    value: any;
    placeholder?: string;
    fullWidth?: boolean;
    multiple?: boolean;
    id?: string;
    isDataLoading?: boolean;
    isDataLoaded?: boolean;
    isDataLoadingFailed?: boolean;
    data: any[];
    className?: string;
    name: string;
    disableClearable?: boolean;
    getOptionLabel: (option: any) => string;
    getOptionDisabled?: (option: any) => boolean;
    renderOption?: (
        props: React.HTMLAttributes<HTMLLIElement>,
        option: any,
        state: AutocompleteRenderOptionState,
    ) => React.ReactNode;
    openOnFocus?: boolean;
    disabled?: boolean;
    required?: boolean;
    blurOnSelect?: boolean;
    readOnly?: boolean;
    freeSolo?: boolean;
    loading?: boolean;
    url?: string;
    method?: "get" | "post" | string;
    payload?: object;
    dataListKey?: string;
    color?: "error" | "primary" | "secondary" | "info" | "success" | "warning" | undefined;
    noDataMessage?: string;
    loadingText?: string;
    defaultData?: any[];
    searchMode?: "clientSide" | "serverSide",
    hasError?: boolean,
    errorMessage?: any,
    onSelectUpdate?: (text: any) => void;
    extraPayload?: object;
    valueKey?: string;
    onBlur?: () => void;
}

const AutoCompleteDropdownComponent = (props: AutoCompleteDropdownComponentProps) => {
        const {
            freeSolo,
            label,
            value,
            hasError,
            required,
            errorMessage,
            onSelectUpdate,
            disabled,
            id,
            data,
            className = '',
            url,
            extraPayload,
            readOnly,
            disableClearable,
            blurOnSelect,
            renderOption,
            getOptionLabel,
            getOptionDisabled,
            onBlur,
            placeholder = "",
            method = "get",
            size = "medium",
            searchMode = "clientSide",
            dataListKey = "data.docs",
            multiple = false,
            fullWidth = true,
            noDataMessage = "No Data",
            // loadingText = "Searching...!",
            color = "secondary",
            openOnFocus = true,
            valueKey,
            isDataLoading,
            isDataLoaded,
            isDataLoadingFailed,
            defaultData = [],
        } = props;

        const [isDropDownDataLoading, setIsDropDownDataLoading] = useState(isDataLoading);
        const [isDropDownDataLoaded, setIsDropDownDataLoaded] = useState(isDataLoaded);
        const [isDropDownDataLoadingFailed, setIsDropDownDataLoadingFailed] = useState(isDataLoadingFailed);
        const [dropDownData, setDropDownData] = useState(data);
        const APICallSubscription = useRef<any>(null);
        const [openPopup, setOpenPopup] = useState<boolean>(false);

        const handleChange = useCallback((event: any, value: any) => {
            let tempValue = value || null;
            if (onSelectUpdate) {
                onSelectUpdate(tempValue)
            }
        }, [onSelectUpdate]);

        useEffect(() => {
            setIsDropDownDataLoading(isDataLoading);
        }, [isDataLoading]);

        useEffect(() => {
            setIsDropDownDataLoaded(isDataLoaded);
        }, [isDataLoaded]);

        useEffect(() => {
            setIsDropDownDataLoadingFailed(isDataLoadingFailed);
        }, [isDataLoadingFailed]);


        useEffect(() => {
            let dropDownData: any[] = [];
            if (data) {
                dropDownData.push(...data);
            }
            setDropDownData(dropDownData);
        }, [data]);


        const filterOptions = useCallback((options: any[], state: FilterOptionsState<any>) => {
            const {getOptionLabel, inputValue} = state;
            const filtered = options.filter((option) => {
                    return getOptionLabel(option).toLowerCase().includes(inputValue.toLowerCase())
                }
            );

            if (filtered.length === 0) {
                return defaultData;
            } else {
                return [...filtered, ...defaultData];
            }
        }, [defaultData]);

        const getDataList = useCallback((searchValue: string) => {
            if (!url) {
                console.warn("URL not provided to fetch dropdown list");
                return;
            }
            if (!method) {
                console.warn("METHOD not provided to fetch dropdown list");
                return;
            }
            const finalPayload = {...extraPayload, search: searchValue};
            const cancelTokenSource = CommonService.getCancelToken();
            let request = null;
            if (method === "get") {
                request = CommonService._api.get
            } else {
                request = CommonService._api.post
            }
            if (APICallSubscription && APICallSubscription.current) {
                APICallSubscription.current.cancel();
            }
            APICallSubscription.current = cancelTokenSource;
            setIsDropDownDataLoading(true);
            setIsDropDownDataLoaded(false);
            setIsDropDownDataLoadingFailed(false);
            let dropDownData: any[] = [...defaultData || []];
            request(url, finalPayload, {}, {cancelToken: cancelTokenSource.token}).then((response: IAPIResponseType<any>) => {
                if (dataListKey && _.get(response, dataListKey)) {
                    dropDownData.unshift(..._.get(response, dataListKey));
                }
                setDropDownData(dropDownData);
                setIsDropDownDataLoading(false);
                setIsDropDownDataLoaded(true);
                setIsDropDownDataLoadingFailed(false);
            }).catch((error: any) => {
                if (error.reason !== AXIOS_REQUEST_CANCELLED) { // if previous request got cancelled do not close loading state
                    setDropDownData(dropDownData);
                    setIsDropDownDataLoading(false);
                    setIsDropDownDataLoaded(false);
                    setIsDropDownDataLoadingFailed(true);
                }
            });
        }, [defaultData, url, dataListKey, method, extraPayload]);

        const handleInputChange = useCallback((event: any, value: any) => {
            if (value) {
                const sanitizedSearchValue = value.trim();
                if (!event || (sanitizedSearchValue?.length === 0 || event?.type === "click")) {
                    return;
                }
                if (sanitizedSearchValue.length === 0) {
                    if (openPopup) setOpenPopup(false);
                } else {
                    if (!openPopup) setOpenPopup(true);
                }
                if (searchMode === "serverSide") {
                    getDataList(sanitizedSearchValue);
                }
            }
        }, [getDataList, openPopup, searchMode]);


        return (
            <FormControl className={`autoComplete-component-wrapper ${className}`}
                         fullWidth color={color}
                         error={hasError} id={id}>
                <Autocomplete
                    fullWidth={fullWidth}
                    open={openPopup}
                    onClose={() => setOpenPopup(false)}
                    noOptionsText={noDataMessage}
                    isOptionEqualToValue={(option, value) => {
                        try {
                            if (valueKey) {
                                return option?.[valueKey] === value?.[valueKey]
                            } else {
                                return JSON.stringify(option) === JSON.stringify(value);
                            }
                        } catch (error) {
                            console.error('Something went wrong in isOptionEqualToValue', error);
                            return false;
                        }
                    }}
                    sx={{
                        "& .MuiOutlinedInput-root": {
                            background: "#FAFAFA"
                        }
                    }
                    }
                    // loadingText={loadingText}
                    options={dropDownData}
                    getOptionLabel={getOptionLabel}
                    renderOption={renderOption}
                    renderInput={(params) => (
                        <TextField {...params} label={label} error={hasError}
                                   required={required} color={color}
                                   placeholder={placeholder}
                                   onClick={() => {
                                       if ((dropDownData && dropDownData.length > 0) && !disabled) {
                                           setOpenPopup(true);
                                       }
                                   }}
                                   onBlur={onBlur}
                                   InputProps={{
                                       ...params.InputProps,
                                       endAdornment: (
                                           <React.Fragment>
                                               {(isDropDownDataLoading && !isDropDownDataLoaded) ?
                                                   <CircularProgress color="inherit" size={20}/> : null}
                                               {params.InputProps.endAdornment}
                                           </React.Fragment>
                                       ),
                                   }}
                        />
                    )}
                    size={size}
                    // value={value || multiple ? [] : ''}
                    // value={formControlValue ||  ''}
                    value={value}
                    freeSolo={freeSolo}
                    multiple={multiple}
                    disableClearable={disableClearable}
                    openOnFocus={openOnFocus}
                    disabled={disabled}
                    getOptionDisabled={getOptionDisabled}
                    blurOnSelect={blurOnSelect}
                    readOnly={readOnly}
                    // loading={isDropDownDataLoading}
                    onChange={handleChange}
                    onInputChange={handleInputChange}
                    onFocus={() => {
                        if (dropDownData && dropDownData.length > 0) {
                            setOpenPopup(true);
                        }
                    }}
                    filterOptions={searchMode === "serverSide" ? (x) => x : filterOptions}
                />
                <FormHelperText>
                    {hasError && <> {errorMessage} </>}
                    {isDropDownDataLoaded && dropDownData && !hasError && !value && dropDownData.length === 0 && <> Data is
                        empty </>}
                    {isDropDownDataLoading && !hasError && <> Data is loading </>}
                    {isDropDownDataLoadingFailed && !hasError && <> Data failed to load </>}
                </FormHelperText>
            </FormControl>
        )
    }

;

export default AutoCompleteDropdownComponent;
